import {
  CombinationColor,
  Item,
  LocationRef,
  Location,
  UserRef,
  ActivityLogChatEntry,
} from '@/redux/back-dashboard-api';
import { DateTime } from 'luxon';
import { getObjectScore } from './object';

type Combination = Required<Omit<Item['combinations'], '__typename'>>;

type WrapValues<T> = {
  [P in keyof T]: Set<NonNullable<T[P]>>;
};

type ItemCombinations = WrapValues<Combination>;

export type ItemWithStock = Item & {
  stock: {
    available: number;
    threshold?: number;
    lendOut: number;
    reserved: number;
    refurbish: number;
    discarded: number;
  };
};

export type Product<K = Item> = {
  location?: LocationRef;
  itemRef: Item;
  combinations: ItemCombinations;
  items: K[];
};

export type ProductWithStock = Product<ItemWithStock> & {
  stock: ItemWithStock['stock'];
};

export const mapItemCombinations = (
  items: Item[],
  location: Location
): Product[] => {
  // Group items by "name", items having the same name are
  // different combinations of the same item.
  const itemCombinations = items.reduce((acc, item) => {
    // Items should be grouped by name and type
    const groupKey = `${item.productName}-${item.type}`;
    acc[groupKey] ||= [];
    acc[groupKey].push(item);

    return acc;
  }, {} as Record<string, Item[]>);

  return Object.values(itemCombinations).map((items) => {
    // Find items with more information.
    // This is due some global items that are loaded from "equipment.ikea.com" lacks of information so we
    // choose the item with more information as reference to show the product details.
    const bestItemCandidate = items.sort((a, b) => {
      const fields = [
        'category',
        'description',
        'documents',
        'imageUrls',
        'properties',
      ] as const;
      const aFields = getObjectScore(a, fields);
      const bFields = getObjectScore(b, fields);
      if (aFields > bFields) return -1;
      if (bFields > aFields) return 1;
      return 0;
    });
    // After sorting, the first item is the one with greatest score
    const itemRef = bestItemCandidate[0];

    return {
      location: itemRef.location ?? undefined,
      itemRef,
      combinations: items.reduce(
        (acc, item) => {
          const combinations = Object.entries(item.combinations).filter(
            ([, value]) => !!value
          );
          for (const [key, value] of combinations) {
            // @ts-ignore
            if (value) acc[key].add(value);
          }
          return acc;
        },
        {
          fit: new Set<string>(),
          size: new Set<string>(),
          color: new Set<CombinationColor>(),
        }
      ),
      items,
    };
  });
};

export const mergeStockWithItems = (
  location: Location,
  products: Product[],
  warehouseId?: string
): ProductWithStock[] =>
  products.map(({ items, ...rest }) => {
    const itemsWithStock = items.map((item) => {
      const stock = location.stock.find(
        ({ item: { _id } }) => _id === item._id
      );
      const {
        availability,
        lendOut = 0,
        reserved = 0,
        refurbish = 0,
        discarded = 0,
      } = stock || {};

      const filteredAvailability = warehouseId
        ? (availability || []).filter((a) => a._warehouseId === warehouseId)
        : availability || [];

      const { available, threshold } = filteredAvailability.reduce(
        (acc, { amount, threshold }) => ({
          available: acc.available + amount,
          threshold: Math.max(acc.threshold, threshold ?? 0), // In case of multiple warehouses, take the highest threshold to indicate the more relevant one
        }),
        { available: 0, threshold: 0 }
      );

      return {
        ...item,
        stock: {
          available: Math.max(0, available - reserved), // Subtract reserved items from available stock
          lendOut,
          reserved,
          refurbish,
          discarded,
          ...(warehouseId && { threshold }), // Threshold is only added when filtering by warehouse
        },
      };
    });

    const stock = itemsWithStock.reduce(
      (
        acc,
        { stock: { reserved, available, refurbish, discarded, lendOut } }
      ) => {
        acc['reserved'] += reserved;
        acc['available'] += available;
        acc['refurbish'] += refurbish;
        acc['discarded'] += discarded;
        acc['lendOut'] += lendOut;
        return acc;
      },
      { reserved: 0, available: 0, refurbish: 0, discarded: 0, lendOut: 0 }
    );

    return {
      ...rest,
      stock,
      items: itemsWithStock,
    };
  });

export const getItemCaption = (
  item: {
    combinations: Item['combinations'];
  },
  t: any
) => {
  const caption = [];
  const { color, fit, size } = item.combinations;

  if (color)
    caption.push(
      t('form.color') + ': ' + t(`label.colorNames.${color.toLowerCase()}`)
    );

  if (fit) caption.push(t('form.fit') + ': ' + fit);

  if (size) caption.push(t('form.size') + ': ' + size);

  return caption.join(' - ');
};

export const groupChangelog = (entries: ActivityLogChatEntry[]) => {
  // Group items by date
  const groupedEntries = entries.reduce((acc, entry) => {
    const dateTime = DateTime.fromMillis(entry.timestamp || 0);
    const date = dateTime.toFormat('yyyy LLL dd');

    acc[date] ||= {
      date,
      dateTime,
      entries: [],
    };
    acc[date]['entries'].push(entry);

    return acc;
  }, {} as Record<string, { date: string; dateTime: DateTime; entries: ActivityLogChatEntry[] }>);

  // Group items by user so entries are grouped together
  return Object.values(groupedEntries)
    .sort((a, b) => a.dateTime.toMillis() - b.dateTime.toMillis())
    .map(({ date, entries }) => {
      const data = {
        date,
        users: [] as {
          user: UserRef;
          entries: ActivityLogChatEntry[];
        }[],
      };

      entries
        .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
        .forEach((entry) => {
          if (
            data.users[data.users.length - 1]?.user?.email === entry.user.email
          ) {
            data.users[data.users.length - 1].entries.push(entry);
          } else {
            data.users.push({
              user: entry.user,
              entries: [entry],
            });
          }
        });

      return data;
    });
};

export const isGlobalItem = (data: { location?: LocationRef | null }) =>
  !data.location;
export const isLocalItem = (data: { location?: LocationRef | null }) =>
  !!data.location;
